import React, { useEffect, useState, useRef } from 'react';
import { useOasisBackend } from '../../hooks/useOasisBackend';
import RheaLoader from './RheaLoader';
import ActionCard from './ActionCard';
import AddActionCard from './AddActionCard';
import type { Action, UserAction } from '../../api/OasisBackendApi';
import { ContentType } from '../../api/OasisBackendApi';

const ActionConfigurator: React.FC = () => {
  const db = useOasisBackend();
  const [enabledActions, setEnabledActions] = useState<UserAction[]>([]);
  const [ownedActions, setOwnedActions] = useState<Action[]>([]);
  const [defaultActions, setDefaultActions] = useState<Action[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAction, setEditingAction] = useState<Action | null>(null);
  const [form, setForm] = useState({ 
    name: '', 
    description: '', 
    public: false,
    instruction: '',
    execution_type: { type: 'url_pattern', url: '', example: '' },
    image_url: ''
  });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [publicActions, setPublicActions] = useState<Action[]>([]);
  const [publicLoading, setPublicLoading] = useState(false);
  const publicFetched = useRef(false);

  const fetchActions = async () => {
    setIsLoading(true);
    try {
      const response = await db.endpoints.rhea.rheaApiActionSettingsRetrieve();
      setEnabledActions(response.data.enabled_actions || []);
      setOwnedActions(response.data.owned_actions || []);
      setDefaultActions(response.data.default_actions || []);
    } catch (error) {
      setEnabledActions([]);
      setOwnedActions([]);
      setDefaultActions([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchActions();
    // eslint-disable-next-line
  }, [db]);

  const handleAddAction = () => {
    setEditingAction(null);
    setShowModal(true);
    setForm({ name: '', description: '', public: false, instruction: '', execution_type: { type: 'url_pattern', url: '', example: '' }, image_url: '' });
    setFormError(null);
  };

  const handleEditAction = (action: Action) => {
    setEditingAction(action);
    setShowModal(true);
    setForm({
      name: action.name,
      description: action.description,
      public: action.public || false,
      instruction: action.instruction,
      execution_type: action.execution_type || { type: 'url_pattern', url: '', example: '' },
      image_url: action.image_url || ''
    });
    setFormError(null);
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' && 'checked' in e.target ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError(null);

    // Validate {query} presence
    if (!form.execution_type.url.includes('{query}')) {
      setFormError('URL pattern must include {query} placeholder');
      setFormLoading(false);
      return;
    }

    if (!form.description.includes('{query}')) {
      setFormError('Description must include {query} placeholder');
      setFormLoading(false);
      return;
    }

    try {
      if (editingAction) {
        await db.endpoints.rhea.rheaApiEditActionUpdate(
          editingAction.id,
          {
            ...(form as any),
            type: ContentType.Json,
          } as any
        );
      } else {
        await db.endpoints.rhea.rheaApiCreateActionCreate({
          ...(form as any),
          type: ContentType.Json,
        } as any);
      }
      setShowModal(false);
      await fetchActions();
    } catch (error: any) {
      setFormError(editingAction ? 'Failed to update action.' : 'Failed to create action.');
    } finally {
      setFormLoading(false);
    }
  };

  // Helper to check if an action is enabled
  const isActionEnabled = (actionId: number) => enabledActions.some(a => a.action === actionId);

  // Placeholder for toggle handler
  const handleToggle = async (actionId: number) => {
    try {
      const response = await db.endpoints.rhea.rheaApiToggleActionCreate({ action_id: actionId });
      setEnabledActions(response.data || []);
    } catch (error) {
      // Optionally handle error
      console.error('Failed to toggle action', error);
    }
  };

  // Fetch public actions only once, on first search
  const fetchPublicActions = async () => {
    setPublicLoading(true);
    try {
      const response = await db.endpoints.rhea.rheaApiPublicActionsList();
      setPublicActions(response.data || []);
      publicFetched.current = true;
    } catch (error) {
      setPublicActions([]);
    } finally {
      setPublicLoading(false);
    }
  };

  // Filter public actions by search
  const filteredPublicActions = search
    ? publicActions.filter(
        (a) =>
          a.name.toLowerCase().includes(search.toLowerCase()) ||
          a.description.toLowerCase().includes(search.toLowerCase())
      )
    : [];

  // Handle search input
  const handleSearchChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    if (!publicFetched.current && e.target.value) {
      await fetchPublicActions();
    }
  };

  // Helper function to get all unique actions
  const getAllUniqueActions = () => {
    const actionMap = new Map<number, Action>();
    
    // Add default actions first (these are public and should be shown to everyone)
    defaultActions.forEach(action => {
      if (action.id) {
        actionMap.set(action.id, action);
      }
    });

    // Add owned actions
    ownedActions.forEach(action => {
      if (action.id) {
        actionMap.set(action.id, action);
      }
    });

    // Add enabled actions that aren't already in the map
    enabledActions.forEach(userAction => {
      if (userAction.action && !actionMap.has(userAction.action)) {
        // Find the action in either owned or default actions
        const action = [...ownedActions, ...defaultActions].find(a => a.id === userAction.action);
        if (action) {
          actionMap.set(userAction.action, action);
        }
      }
    });

    return Array.from(actionMap.values());
  };

  if (isLoading) {
    return <RheaLoader />;
  }

  return (
    <div 
      className="container mx-auto py-12 min-h-screen"
      style={{ backgroundColor: 'var(--rhea-bg-primary)' }}
    >
      <h1 
        className="text-3xl font-bold text-center mb-8"
        style={{ color: 'var(--rhea-text-primary)' }}
      >
        Action Configurator
      </h1>
      <div className="max-w-md mx-auto mb-6">
        <input
          type="text"
          value={search}
          onChange={handleSearchChange}
          placeholder="Search public actions..."
          className="w-full px-4 py-2 rounded focus:outline-none focus:ring-2"
          style={{ 
            backgroundColor: 'var(--rhea-bg-tertiary)',
            color: 'var(--rhea-text-primary)',
            border: '1px solid var(--rhea-card-border)'
          }}
        />
      </div>
      {publicLoading && search && (
        <div 
          className="text-center mb-4"
          style={{ color: 'var(--rhea-text-secondary)' }}
        >
          Loading public actions...
        </div>
      )}
      {filteredPublicActions.length > 0 && (
        <div className="flex flex-col gap-6 px-2 mb-8">
          {filteredPublicActions.map((action) => (
            <ActionCard
              key={action.id}
              action={action}
              enabled={isActionEnabled(action.id)}
              onToggle={handleToggle}
            />
          ))}
        </div>
      )}
      <div className="flex flex-col gap-6 px-2">
        <AddActionCard onClick={handleAddAction} />
        {getAllUniqueActions().map((action, idx) => (
          <ActionCard 
            key={action.id || idx} 
            action={action} 
            enabled={isActionEnabled(action.id)} 
            onToggle={handleToggle}
            onEdit={() => handleEditAction(action)}
            isOwned={ownedActions.some(a => a.id === action.id)}
          />
        ))}
      </div>
      {showModal && (
        <div 
          className="fixed inset-0 flex items-center justify-center z-50"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)' }}
        >
          <form
            onSubmit={handleFormSubmit}
            className="rounded-lg shadow-lg p-8 w-full max-w-md relative"
            style={{ 
              backgroundColor: 'var(--rhea-card-bg)',
              border: '1px solid var(--rhea-card-border)'
            }}
          >
            <button
              type="button"
              className="absolute top-2 right-2 text-2xl hover:opacity-80"
              onClick={() => setShowModal(false)}
              aria-label="Close"
              style={{ color: 'var(--rhea-text-secondary)' }}
            >
              &times;
            </button>
            <h2 
              className="text-xl font-bold mb-4"
              style={{ color: 'var(--rhea-text-primary)' }}
            >
              {editingAction ? 'Edit Action' : 'Create New Action'}
            </h2>
            <div className="mb-4">
              <label 
                className="block mb-1"
                htmlFor="name"
                style={{ color: 'var(--rhea-text-secondary)' }}
              >
                Name
              </label>
              <input
                id="name"
                name="name"
                type="text"
                value={form.name}
                onChange={handleFormChange}
                className="w-full rounded px-3 py-2 focus:outline-none focus:ring-2"
                style={{ 
                  backgroundColor: 'var(--rhea-bg-tertiary)',
                  color: 'var(--rhea-text-primary)',
                  border: '1px solid var(--rhea-card-border)'
                }}
                required
                maxLength={255}
              />
            </div>
            <div className="mb-4">
              <label 
                className="block mb-1"
                htmlFor="description"
                style={{ color: 'var(--rhea-text-secondary)' }}
              >
                Description
              </label>
              <textarea
                id="description"
                name="description"
                value={form.description}
                onChange={handleFormChange}
                className="w-full rounded px-3 py-2 focus:outline-none focus:ring-2"
                style={{ 
                  backgroundColor: 'var(--rhea-bg-tertiary)',
                  color: 'var(--rhea-text-primary)',
                  border: '1px solid var(--rhea-card-border)'
                }}
                required
                maxLength={255}
              />
            </div>
            <div className="mb-4">
              <label 
                className="block mb-1"
                htmlFor="image_url"
                style={{ color: 'var(--rhea-text-secondary)' }}
              >
                Image URL
              </label>
              <input
                id="image_url"
                name="image_url"
                type="url"
                value={form.image_url}
                onChange={handleFormChange}
                className="w-full rounded px-3 py-2 focus:outline-none focus:ring-2"
                style={{ 
                  backgroundColor: 'var(--rhea-bg-tertiary)',
                  color: 'var(--rhea-text-primary)',
                  border: '1px solid var(--rhea-card-border)'
                }}
                placeholder="Enter image URL..."
              />
            </div>
            <div className="mb-4 flex items-center">
              <input
                id="public"
                name="public"
                type="checkbox"
                checked={form.public}
                onChange={handleFormChange}
                className="h-5 w-5 focus:ring-2"
                style={{ 
                  backgroundColor: 'var(--rhea-bg-tertiary)',
                  borderColor: 'var(--rhea-card-border)'
                }}
              />
              <label 
                htmlFor="public" 
                className="ml-2 select-none cursor-pointer"
                style={{ color: 'var(--rhea-text-secondary)' }}
              >
                Make public
              </label>
            </div>
            <div className="mb-4">
              <label 
                className="block mb-1"
                htmlFor="instruction"
                style={{ color: 'var(--rhea-text-secondary)' }}
              >
                Instruction
              </label>
              <textarea
                id="instruction"
                name="instruction"
                value={form.instruction}
                onChange={handleFormChange}
                className="w-full rounded px-3 py-2 focus:outline-none focus:ring-2"
                style={{ 
                  backgroundColor: 'var(--rhea-bg-tertiary)',
                  color: 'var(--rhea-text-primary)',
                  border: '1px solid var(--rhea-card-border)'
                }}
                required
                maxLength={200}
                placeholder="Enter instruction for the action..."
              />
              <div 
                className="text-sm mt-1"
                style={{ color: 'var(--rhea-text-secondary)' }}
              >
                {form.instruction.length}/200 characters
              </div>
            </div>
            <div className="mb-4">
              <label 
                className="block mb-1"
                htmlFor="execution_type"
                style={{ color: 'var(--rhea-text-secondary)' }}
              >
                Execution Type
              </label>
              <div className="space-y-2">
                <input
                  type="text"
                  name="execution_type.url"
                  value={form.execution_type.url}
                  onChange={(e) => setForm(prev => ({
                    ...prev,
                    execution_type: { ...prev.execution_type, url: e.target.value }
                  }))}
                  className="w-full rounded px-3 py-2 focus:outline-none focus:ring-2"
                  style={{ 
                    backgroundColor: 'var(--rhea-bg-tertiary)',
                    color: 'var(--rhea-text-primary)',
                    border: '1px solid var(--rhea-card-border)'
                  }}
                  placeholder="Enter URL pattern with {query} placeholders..."
                  required
                />
                <input
                  type="text"
                  name="execution_type.example"
                  value={form.execution_type.example}
                  onChange={(e) => setForm(prev => ({
                    ...prev,
                    execution_type: { ...prev.execution_type, example: e.target.value }
                  }))}
                  className="w-full rounded px-3 py-2 focus:outline-none focus:ring-2"
                  style={{ 
                    backgroundColor: 'var(--rhea-bg-tertiary)',
                    color: 'var(--rhea-text-primary)',
                    border: '1px solid var(--rhea-card-border)'
                  }}
                  placeholder="Enter example URL..."
                  required
                />
              </div>
            </div>
            {formError && (
              <div 
                className="mb-2"
                style={{ color: 'var(--rhea-text-error)' }}
              >
                {formError}
              </div>
            )}
            <button
              type="submit"
              className="w-full py-2 rounded hover:opacity-90 transition-opacity disabled:opacity-50"
              style={{ 
                backgroundColor: 'var(--rhea-accent-primary)',
                color: 'var(--rhea-text-primary)'
              }}
              disabled={formLoading}
            >
              {formLoading ? (editingAction ? 'Updating...' : 'Creating...') : (editingAction ? 'Update Action' : 'Create Action')}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default ActionConfigurator; 